/**
 * `GET /v1/me` di Limulo → il record utente canonico dello store (`AuthUser`).
 *
 * ⚠️ PERCHE' ESISTE QUESTO FILE, invece di una `transformResponse` dentro
 * `limuloApi.ts`. Fino a oggi il boot della TMA chiamava SEMPRE il monolite Laravel
 * (`useLogtoAuth.ts` → `useGetMeQuery` → `GET /users/me`), **dietro nessun flag**: una
 * build con la sola sezione Limulo accesa restava comunque legata a Laravel per l'unica
 * chiamata che tocca ogni utente e ogni schermata. Misurato il 2026-09-05 su
 * `stg-tma.qlash.gg` col monolite di staging giu' (503 da API Gateway): la sezione
 * `/limulo/*` rendeva dati veri mentre il boot bruciava ~19 s di rete per schermata su
 * una chiamata di cui non aveva bisogno.
 *
 * La derivazione sta QUI e non nel client perche' deve essere esercitabile da
 * `tools/verify-limulo.ts` sotto Node: niente `import.meta.env`, niente `@/env`, niente
 * lettura di `localStorage`. **I ruoli arrivano come parametro**, non li legge lei.
 *
 * 🔑 LA REGOLA CHE QUESTO FILE ESISTE PER NON FAR DIMENTICARE: `is_admin` e
 * `beta_tester` **non vengono dal backend** — ne' da Laravel ne' da Limulo. Vengono dal
 * claim `roles` dell'access token Logto, ed e' gia' cosi' sul percorso Laravel
 * (`services/endpoints/auth.ts:28-37`). Qui si specchia quella derivazione **identica**,
 * per una ragione di sicurezza e non di simmetria:
 *
 *   ⛔ `me.user.role` e `me.membership.role` di Limulo (OWNER/ADMIN/USER/GUEST) sono uno
 *      spazio di autorita' DIVERSO — dicono cosa puo' fare quell'utente dentro
 *      l'organizzazione Limulo, non se e' un amministratore QLASH. Mapparli su `is_admin`
 *      significherebbe che un OWNER di una qualunque organizzazione Limulo diventa admin
 *      della TMA. Non e' un rischio teorico: su un tenant multi-organizzazione il ruolo
 *      OWNER e' esattamente cio' che un cliente si autoassegna.
 *
 * Nota di portata, perche' non venga sopravvalutata: oggi `is_admin`/`beta_tester` non
 * sono letti da nessun gate — `AdminGate` (`components/common/AdminGate.tsx:44-50`) e
 * `useHasLogtoRole` (`:45-48`) leggono i ruoli Logto per conto proprio. I due campi
 * restano sulla forma per i consumatori che li leggono per nome. Il divieto sopra vale
 * comunque: e' scritto per il giorno in cui qualcuno li ricollegasse a un gate.
 */

import type { AuthUser } from '../../store/auth.slice.ts';
import type { LimuloMe } from '../../types/limulo.ts';
import { nicknameQlashDaMe } from '../qgammon/displayName.ts';

/**
 * Le stringhe vuote di Limulo non sono valori: `avatar: ''` messo in un `src` produce
 * una richiesta all'origine corrente, e `name: ''` un'intestazione vuota che sembra un
 * caricamento. `undefined` fa cadere i consumatori sul loro fallback, che e' cio' che
 * gia' fanno per i campi che Laravel omette.
 */
const orUndefined = (value: string | undefined | null): string | undefined =>
  value && value.trim() !== '' ? value : undefined;

/**
 * Converte la risposta di `GET /v1/me` nella forma canonica dello store.
 *
 * @param me      il payload gia' spacchettato dall'envelope (`null` se assente)
 * @param logtoRoles il claim `roles` dell'access token Logto — **parametro**, mai letto qui
 *
 * Ritorna `null` quando non c'e' payload: chi chiama deve poter distinguere «non ancora
 * caricato» da «caricato vuoto», e un `AuthUser` con campi tutti `undefined` sarebbe
 * indistinguibile da un utente vero mal popolato.
 */
export function limuloMeToAuthUser(
  me: LimuloMe | null | undefined,
  logtoRoles: readonly string[],
): AuthUser | null {
  if (!me?.user) return null;

  return {
    // ⚠️ ObjectId stringa, MAI convertito. `AuthUser.id` e' gia' `number | string`
    // (`store/auth.slice.ts:16`), quindi la forma regge senza modifiche; ma un
    // `Number(id)` a valle darebbe `NaN`, che e' il difetto descritto nel progetto del
    // collasso (PR #99 §1.3) per i link `/tournaments/${id}`.
    id: me.user.id,
    // Limulo espone UN solo nome (`user.name`), Laravel ne aveva due (`nickname` →
    // `username`, `name` → `firstName`). Lo mettiamo in `username`, che e' il campo che
    // i consumatori vivi leggono (`hooks/useProfileSync.ts:34`), e lasciamo `firstName`
    // assente invece di duplicarlo: un nome ripetuto in due campi si propaga come se
    // fossero due dati distinti, e nessuno poi sa quale sia quello vero.
    //
    // 🔴 Ma `name` senza una scelta e' il SEGNAPOSTO `"Player"` (ADR-0125, `types/limulo.ts`):
    // copiato qui diventava il nickname dell'utente — il campo delle Impostazioni partiva
    // precompilato «Player» [M `tma-orc-5-nickname`, stg-tma, 2026-09-19 20:48Z]. ⇒ il nome
    // scelto viene da `nickname`; con `requiresNickname: true` non c'e' un nome da mostrare.
    // `name` resta solo per la facciata che il campo non lo espone (`requiresNickname`
    // assente), dove era ed e' l'unico nome disponibile.
    username:
      orUndefined(me.user.nickname) ??
      (me.user.requiresNickname === true ? undefined : orUndefined(me.user.name)),
    // ⚠️ Passa attraverso INTATTO, `undefined` compreso: e' il predicato con cui
    // `useProfileSync` decide se pubblicare, e un default a `false` qui direbbe «ha scelto
    // un nome» a nome di una facciata che non ha risposto.
    requiresNickname: me.user.requiresNickname,
    // Il nome pubblico del backgammon: il nickname e basta, mai `username` (che ripiega sul `name`).
    nicknameQlash: nicknameQlashDaMe(me),
    email: orUndefined(me.user.email),
    avatarUrl: orUndefined(me.user.avatar),
    // Vedi il blocco in testa al file: dai ruoli Logto, MAI da `me.user.role` o
    // `me.membership.role`.
    is_admin: logtoRoles.includes('administrator'),
    beta_tester: logtoRoles.includes('beta'),
  };
}
